import { subscribeToAuth } from "@/common/lib/auth";
import { firebaseReady, getFirebaseAuth } from "@/common/lib/firebase";
import type { User } from "firebase/auth";
import { useEffect, useState } from "react";

export type AuthState =
  | { status: "loading"; user: null }
  | { status: "authenticated"; user: User }
  | { status: "unauthenticated"; user: null };

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  useEffect(() => {
    if (!firebaseReady) {
      setState({ status: "unauthenticated", user: null });
      return;
    }

    let cancelled = false;
    let pollStop: (() => void) | null = null;

    const unsubscribe = subscribeToAuth((user) => {
      if (user) {
        pollStop?.();
        pollStop = null;
        setState({ status: "authenticated", user });
        return;
      }

      // Firebase dijo null — puede ser que todavía no terminó el restore de
      // IndexedDB. Sondeamos hasta 1.5s antes de rendirnos, así evitamos el
      // flash de login screen en reload.
      let attempts = 0;
      const maxAttempts = 5;
      const iv = setInterval(() => {
        attempts += 1;
        if (cancelled) {
          clearInterval(iv);
          return;
        }
        const current = getFirebaseAuth().currentUser;
        if (current) {
          clearInterval(iv);
          pollStop?.();
          pollStop = null;
          setState({ status: "authenticated", user: current });
          return;
        }
        if (attempts >= maxAttempts) {
          clearInterval(iv);
          pollStop?.();
          pollStop = null;
          setState({ status: "unauthenticated", user: null });
        }
      }, 300);
      pollStop = () => clearInterval(iv);
    });

    return () => {
      cancelled = true;
      pollStop?.();
      unsubscribe();
    };
  }, []);

  return state;
}
